// src/components/transactions/TransactionDetailsModal.jsx
import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  X, 
  CheckCircle, 
  Clock, 
  Play, 
  AlertCircle,
  User,
  Store,
  Package,
  CreditCard,
  Printer,
  Download
} from 'lucide-react';

const TransactionDetailsModal = ({ transaction, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  const getStatusStyle = (status) => {
    switch (status) {
      case 'VALIDATED':
        return { 
          bgColor: 'bg-green-100', 
          textColor: 'text-green-800',
          icon: <CheckCircle size={16} className="mr-1 text-green-600" />,
          label: 'Validée'
        };
      case 'PENDING':
        return { 
          bgColor: 'bg-yellow-100', 
          textColor: 'text-yellow-800',
          icon: <Clock size={16} className="mr-1 text-yellow-600" />,
          label: 'En attente'
        };
      case 'IN_PROGRESS':
        return { 
          bgColor: 'bg-blue-100', 
          textColor: 'text-blue-800',
          icon: <Play size={16} className="mr-1 text-blue-600" />,
          label: 'En cours'
        };
      case 'CANCELLED':
        return { 
          bgColor: 'bg-red-100', 
          textColor: 'text-red-800',
          icon: <AlertCircle size={16} className="mr-1 text-red-600" />,
          label: 'Annulée'
        };
      default:
        return { 
          bgColor: 'bg-gray-100', 
          textColor: 'text-gray-800',
          icon: null,
          label: status
        };
    }
  };
  
  const statusStyle = getStatusStyle(transaction.status);
  
  return (
    <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Détails de la Transaction
          </h2>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-500"
            onClick={onClose}
          >
            <span className="sr-only">Close</span>
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-6 py-4">
          {/* Status and ID */}
          <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
            <div className="flex items-center">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bgColor} ${statusStyle.textColor}`}>
                {statusStyle.icon}
                {statusStyle.label}
              </span>
              
              <span className="ml-4 text-sm text-gray-500">
                ID: <span className="font-mono">{transaction.id}</span>
              </span>
            </div>
            
            <div className="flex space-x-2">
              <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50">
                <Printer size={16} className="mr-1.5" />
                Imprimer
              </button>
              <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm rounded-md text-white bg-green-700 hover:bg-green-800">
                <Download size={16} className="mr-1.5" />
                Télécharger le reçu
              </button>
            </div>
          </div>
          
          {/* Grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Transaction details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider">Informations générales</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Date</span>
                  <span className="text-sm font-medium text-gray-900">
                    {format(new Date(transaction.date), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Type</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    transaction.type === 'SCAN' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {transaction.type === 'SCAN' ? 'Scan QR' : 'Retrait'}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Montant</span>
                  <span className="text-sm font-medium text-green-600">
                    {transaction.gain.toFixed(2)} DH
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Commission</span>
                  <span className="text-sm font-medium text-gray-900">
                    {transaction.companyCommission.toFixed(2)} DH
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Solde avant</span>
                  <span className="text-sm font-medium text-gray-900">
                    {transaction.avantGain.toFixed(2)} DH
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Solde après</span>
                  <span className="text-sm font-medium text-gray-900">
                    {transaction.apresGain.toFixed(2)} DH
                  </span>
                </div>
              </div>
            </div>
            
            {/* User info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider flex items-center">
                <User size={16} className="mr-1.5" />
                Utilisateur
              </h3>
              
              {transaction.user ? (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Nom</span>
                    <span className="text-sm font-medium text-gray-900">
                      {transaction.user.firstName} {transaction.user.lastName}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Email</span>
                    <span className="text-sm font-medium text-gray-900">
                      {transaction.user.email}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Type</span>
                    <span className="text-sm font-medium text-gray-900">
                      {transaction.user.userType === 'PHARMACIST' ? 'Pharmacien' : 'Vendeur'}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Information utilisateur non disponible</p>
              )}
            </div>
            
            {/* Product info */}
            {transaction.type === 'SCAN' && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider flex items-center">
                  <Package size={16} className="mr-1.5" />
                  Produit
                </h3>
                
                {transaction.product ? (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Nom</span>
                      <span className="text-sm font-medium text-gray-900">
                        {transaction.product.name}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Prix</span>
                      <span className="text-sm font-medium text-gray-900">
                        {transaction.product.price.toFixed(2)} DH
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Code QR</span>
                      <span className="text-sm font-medium text-gray-900">
                        {/* Would show QR code ID in real app */}
                        QR-{transaction.id.substring(0, 8)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Information produit non disponible</p>
                )}
              </div>
            )}
            
            {/* Pharmacy info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider flex items-center">
                <Store size={16} className="mr-1.5" />
                Pharmacie
              </h3>
              
              {transaction.pharmacy ? (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Nom</span>
                    <span className="text-sm font-medium text-gray-900">
                      {transaction.pharmacy.namePharmacy}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Téléphone</span>
                    <span className="text-sm font-medium text-gray-900">
                      {transaction.pharmacy.telPharmacy}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Information pharmacie non disponible</p>
              )}
            </div>
          </div>
          
          {/* Timeline */}
          <div className="mt-8">
            <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider">Chronologie</h3>
            
            <div className="relative border-l-2 border-gray-200 pl-5 space-y-6 pt-2 pb-2">
              {/* Created event */}
              <div className="relative">
                <div className="absolute -left-7 flex items-center justify-center w-5 h-5 bg-blue-100 rounded-full ring-4 ring-white">
                  <CreditCard size={12} className="text-blue-600" />
                </div>
                <div>
                  <time className="block text-xs text-gray-500">
                    {format(new Date(transaction.createdAt), 'dd MMM yyyy à HH:mm', { locale: fr })}
                  </time>
                  <h4 className="text-sm font-medium text-gray-900">Transaction créée</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Transaction de type {transaction.type === 'SCAN' ? 'Scan QR' : 'Retrait'} enregistrée sur la plateforme
                  </p>
                </div>
              </div>
              
              {/* Status change event - in a real app this would show all status changes */}
              <div className="relative">
                <div className="absolute -left-7 flex items-center justify-center w-5 h-5 rounded-full ring-4 ring-white" 
                     style={{ backgroundColor: transaction.status === 'VALIDATED' ? '#DEF7EC' : 
                                            transaction.status === 'PENDING' ? '#FEF3C7' :
                                            transaction.status === 'IN_PROGRESS' ? '#DBEAFE' : '#FEE2E2' }}>
                  {statusStyle.icon}
                </div>
                <div>
                  <time className="block text-xs text-gray-500">
                    {format(new Date(transaction.updatedAt), 'dd MMM yyyy à HH:mm', { locale: fr })}
                  </time>
                  <h4 className="text-sm font-medium text-gray-900">Statut mis à jour</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    La transaction est maintenant "{statusStyle.label}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer with action buttons */}
        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2 border-t border-gray-200">
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            onClick={onClose}
          >
            Fermer
          </button>
          
          {transaction.status === 'PENDING' && (
            <button
              type="button"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-700 hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              onClick={() => {
                alert(`Valider la transaction: ${transaction.id}`);
                onClose();
              }}
            >
              Valider
            </button>
          )}
          
          {transaction.status === 'IN_PROGRESS' && (
            <button
              type="button"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-700 hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              onClick={() => {
                alert(`Valider la transaction: ${transaction.id}`);
                onClose();
              }}
            >
              Valider
            </button>
          )}
          
          {(transaction.status === 'PENDING' || transaction.status === 'IN_PROGRESS') && (
            <button
              type="button"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={() => {
                alert(`Annuler la transaction: ${transaction.id}`);
                onClose();
              }}
            >
              Annuler
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;